import type { Preset, TenantBinding, TenantBindingView } from '@/services/tenantApi';
/**
 * Space-scoped content grants in the UI.
 *
 * The backend enforces per-space scopes (see `common.tenancy`); the frontend
 * mirrors them for UX only — hiding surfaces and nav the caller cannot use.
 * Grants arrive on each membership from GET /auth/v1/tenants as
 * `{ space_id: [scope, ...] }` ('*' is the open-space baseline), resolved
 * server-side with the same rules that mint the token's custom:grants.
 *
 * Fail-open by design: when `grants` is absent (shared-auth not yet returning
 * it), everything stays visible — the backend still denies, the UI just shows
 * doors that won't open. Deny-by-default here would brick the app on a stale
 * auth deploy.
 */

import type { SurfaceKey } from '@systemdox/surface-header';

export type Grants = Record<string, string[]>;

/** True when the caller holds `scope` in ANY space (UI-level "can use at all"). */
export function hasScope(grants: Grants | null | undefined, scope: string): boolean {
  if (!grants) return true; // fail open: gating activates once grants arrive
  return Object.values(grants).some((scopes) => scopes.includes(scope));
}

/** True when the caller holds `scope` in `spaceId`, directly or through the open-space `*` baseline. */
export function hasSpaceScope(grants: Grants | null | undefined, spaceId: string, scope: string): boolean {
  if (!grants) return true; // fail open, as hasScope
  return [...(grants[spaceId] ?? []), ...(grants['*'] ?? [])].includes(scope);
}

/** Scope prefix per surface. Keys are the legacy SurfaceKey names:
 *  'app' = Capture, 'console' = Build, 'docs' = Document. */
const SURFACE_PREFIX: Record<SurfaceKey, string> = {
  app: 'capture:',
  console: 'build:',
  docs: 'docs:',
};

/**
 * Scopes that reach a surface's HOST without holding any of that surface's own
 * scopes, because the host also serves another surface's content. Hosts are not
 * surfaces: #773 moved the Docs page — and with it the Requirements and Design
 * pillars, which are Build content (`build:read`, as GET /specs enforces) — onto
 * the capture host, and they are browsable nowhere else. Without this the
 * Developer persona (Build + Document, no Capture) has no door to the specs it
 * exists to implement. Same rule CaptureShell uses to show its Docs tab.
 *
 * `docs:read` is deliberately absent: a Reader's surface is the published docs
 * portal, and GET /specs returns them nothing here.
 */
const SURFACE_HOSTED_SCOPES: Partial<Record<SurfaceKey, string[]>> = {
  app: ['build:read'],
};

/** True when the caller holds any scope on the surface — or on content that
 *  surface's host carries — in any space. */
export function hasSurfaceAccess(grants: Grants | null | undefined, surface: SurfaceKey): boolean {
  if (!grants) return true;
  const prefix = SURFACE_PREFIX[surface];
  if (Object.values(grants).some((scopes) => scopes.some((s) => s.startsWith(prefix)))) return true;
  return (SURFACE_HOSTED_SCOPES[surface] ?? []).some((s) => hasScope(grants, s));
}

/*
 * Presets — the catalogue GET /presets serves (shared-auth step 7, ADR-061):
 * named scope bundles a picker offers; assigning one on the open baseline
 * ('*') is the whole picker story for a single-space workspace. The helpers
 * below take the catalogue as a parameter rather than reading a module-level
 * copy: a hand-maintained mirror is exactly what step 7 deletes. And the
 * catalogue is not authority — a binding stores the scopes it names, and the
 * server's resolver never reads the label — so a label never outranks the
 * scopes beside it here either.
 */

/** Set-equality of two scope lists; order and repeats are immaterial. */
function sameScopes(a: readonly string[], b: readonly string[]): boolean {
  const want = new Set(a);
  const have = new Set(b);
  return want.size === have.size && [...want].every((s) => have.has(s));
}

/** The catalogue entry `slug` names, if any. */
function presetEntry(slug: string | undefined, presets: readonly Preset[]): Preset | undefined {
  return slug ? presets.find((p) => p.slug === slug) : undefined;
}

/** The scopes a stored row carries, or undefined when it names none: an empty
 *  list is not a grant (the server refuses to store one). */
function rowScopes(b: TenantBindingView): string[] | undefined {
  return b.scopes?.length ? b.scopes : undefined;
}

/** The slug a stored binding names, whichever key carries it. */
export function bindingSlug(b: { persona?: string; preset?: string }): string | undefined {
  return b.preset ?? b.persona;
}

/**
 * The catalogue slug a stored binding amounts to, or undefined for custom
 * scopes and labels the catalogue does not know. When the row carries
 * `scopes` they decide alone; its `preset` / legacy `persona` label is read
 * only when there are no scopes to read (rows written before step 7), so a
 * label can never claim more — or less — than the grant beside it.
 */
export function bindingPreset(b: TenantBindingView, presets: readonly Preset[]): string | undefined {
  const scopes = rowScopes(b);
  if (scopes) return presets.find((p) => sameScopes(p.scopes, scopes))?.slug;
  return presetEntry(bindingSlug(b), presets)?.slug;
}

/**
 * The preset a member's bindings amount to: exactly one binding on the open
 * baseline, not expiring, that names a catalogue preset → that slug; anything
 * else (multi-space, space-bound, expiring, custom scopes, an unknown label)
 * → 'custom', shown read-only so the picker never silently clobbers a
 * deliberate setup. Null when bindings are absent or empty.
 */
export function presetFromBindings(
  bindings: TenantBindingView[] | undefined | null,
  presets: readonly Preset[],
): string | 'custom' | null {
  if (!bindings || bindings.length === 0) return null;
  const [only] = bindings;
  if (bindings.length !== 1 || only.space !== '*' || only.expiresAt) return 'custom';
  return bindingPreset(only, presets) ?? 'custom';
}

/** Display label for a catalogue slug; the raw slug when the catalogue lacks it. */
export function presetLabel(slug: string, presets: readonly Preset[]): string {
  return presetEntry(slug, presets)?.label ?? slug;
}

/**
 * A write binding copied from a catalogue preset: the entry's scopes, and its
 * slug as the label the server checks them against. Throws on a slug the
 * catalogue lacks — the server would refuse the empty grant anyway
 * (invalid_scopes), and a picker offering it is a bug worth hearing about.
 */
export function presetBinding(space: string, slug: string, presets: readonly Preset[]): TenantBinding {
  const entry = presetEntry(slug, presets);
  if (!entry) throw new Error(`Unknown access preset "${slug}": not in the catalogue.`);
  return { space, scopes: [...entry.scopes], preset: entry.slug };
}

/**
 * A stored binding mapped to the write shape. `scopes` pass through as the
 * grant, carrying the row's `preset` only when the catalogue agrees it names
 * exactly those scopes (a stale label would turn the write into a 400
 * invalid_preset; no label is invented either). A row with no scopes —
 * written before step 7 — maps its legacy persona/preset slug to the
 * catalogue scopes. Anything else throws: refusing loudly beats echoing a
 * narrower grant back to the server, which is what a silent default would do.
 */
export function toWriteBinding(b: TenantBindingView, presets: readonly Preset[]): TenantBinding {
  const expiry = b.expiresAt ? { expiresAt: b.expiresAt } : {};
  const scopes = rowScopes(b);
  if (scopes) {
    const labelled = presetEntry(b.preset, presets);
    const label = labelled && sameScopes(labelled.scopes, scopes) ? { preset: labelled.slug } : {};
    return { space: b.space, scopes: [...scopes], ...label, ...expiry };
  }
  const entry = presetEntry(bindingSlug(b), presets);
  if (!entry) {
    throw new Error(
      'This access holds custom scopes; the row names neither its scopes nor a catalogue preset, so it cannot be rewritten from here.',
    );
  }
  return { space: b.space, scopes: [...entry.scopes], preset: entry.slug, ...expiry };
}

/**
 * Filter a nav tree by grants: an item survives when it has no mapped scope
 * (always visible) or the caller holds ANY of the mapped scopes somewhere —
 * a destination that hosts content from several surfaces (Docs carries the
 * Requirements/Design pillars) maps to one scope per surface. Children are
 * filtered with the same rule; a parent that fails its own scopes drops
 * with all its children.
 */
export function filterNavByGrants<T extends { to: string; children?: T[] }>(
  items: T[],
  grants: Grants | null | undefined,
  scopeFor: (to: string) => string | string[] | undefined,
): T[] {
  return items
    .filter((item) => {
      const scope = scopeFor(item.to);
      if (!scope) return true;
      return (Array.isArray(scope) ? scope : [scope]).some((s) => hasScope(grants, s));
    })
    .map((item) =>
      item.children
        ? { ...item, children: filterNavByGrants(item.children, grants, scopeFor) }
        : item,
    );
}
